import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { initI18n } from '@lb/i18n'
import '@lb/ui/styles.css'
import '@fontsource/rosario/400.css'
import '@fontsource/rosario/500.css'
/*
 * Rosario's REAL italics, for the pull-quotes and standfirsts only.
 *
 * Without them the browser slanted the upright face wherever `italic` was
 * used, and that faux italic is what the client read as an off-brand "script
 * font" on the member's name (punch-list item 1). The name no longer slants
 * at all (D5); quotes keep their italic and now get the face the guide
 * actually allows. Both weights the product sets serif text at, so no italic
 * is ever satisfied by a synthesised one — `font-synthesis: none` in the
 * design system's base makes sure of it. Inter Tight has NO italic loaded
 * on purpose: nothing in the sans face is italic any more.
 */
import '@fontsource/rosario/400-italic.css'
import '@fontsource/rosario/500-italic.css'
import '@fontsource/inter-tight/400.css'
import '@fontsource/inter-tight/500.css'
import '@fontsource/inter-tight/600.css'
import '@fontsource/inter-tight/700.css'

import { env } from './env'
import { App } from './app/app'
import { AppProviders } from './app/providers/app-providers'

async function start() {
  /*
   * Nothing before `render` may be able to prevent it.
   *
   * This awaited `worker.start()` bare, and when the service worker failed to
   * register the rejection propagated out of `start()` and the app never
   * rendered at all — a blank cream page with one console line, which type
   * checks, builds and unit tests all passed straight through.
   *
   * So the mock transport is best-effort and says so loudly. An app running
   * against a dead transport shows its own error states, which is a diagnosis;
   * a white screen is not.
   */
  /*
   * `import.meta.env.DEV &&` is what keeps MSW out of the production bundle.
   *
   * The dynamic import alone was not enough: Vite still emitted the chunk, so
   * the deploy artifact carried 331 kB (107 kB gzipped) of mock transport plus
   * every fixture — member names, post bodies, plan prices — as a static file
   * on the origin. Nothing fetched it, which is exactly why nobody would
   * notice it was there.
   *
   * `DEV` is statically false in a production build, so Rollup drops the
   * branch and the chunk with it. Mocks only ever run under `dev:mocks`, which
   * is a dev server, so nothing is lost.
   */
  if (import.meta.env.DEV && env.VITE_USE_MOCKS) {
    try {
      const { worker } = await import('./mocks/browser')
      await worker.start({ onUnhandledRequest: 'bypass' })
    } catch (error) {
      console.error(
        '[lb] MSW failed to start — the app is running with NO transport and every ' +
          'request will fail. If mockServiceWorker.js is missing, run `pnpm exec msw init public/`.',
        error,
      )
    }
  }

  // Copy is never inline (§11), so i18n resolves before the first render rather
  // than flashing keys.
  await initI18n()

  const root = document.getElementById('root')
  if (!root) throw new Error('#root is missing from index.html')

  createRoot(root).render(
    <StrictMode>
      <AppProviders>
        <App />
      </AppProviders>
    </StrictMode>,
  )
}

void start()
